import { EntityConfig } from './entity-registry';
import { GenericTableColumn, GenericTableAction } from '../../../../../models/GenericCRUD/GenericTable';
import { GenericFormField } from '../../../../../models/GenericCRUD/GenericForm';
import { Filter } from '../../../../../models/GenericCRUD/Filter';

// Configuración para Facturas (emitidas, recibidas, proformas)
const tableColumns: GenericTableColumn[] = [
  { name: 'ID', active: false, type: 'text', key: 'id', sort: true },
  { name: 'Número', active: true, type: 'text', key: 'invoice_number', sort: true },
  { name: 'Fecha', active: true, type: 'date', key: 'invoice_date', sort: true },
  {
    name: 'Tipo', active: true, type: 'badge', key: 'invoice_type', sort: true,
    labelMap: { 'issued': 'A emitir', 'received': 'A recibir', 'proforma': 'Proforma' },
    colorMap: { 'issued': 'bg-blue-100 text-blue-800', 'received': 'bg-purple-100 text-purple-800', 'proforma': 'bg-yellow-100 text-yellow-800' }
  },
  { name: 'Alumno', active: true, type: 'text', key: 'user.name', sort: false },
  { name: 'Base Imponible', active: true, type: 'currency', key: 'subtotal', sort: true },
  { name: 'Total', active: true, type: 'currency', key: 'total', sort: true },
  {
    name: 'Estado', active: true, type: 'badge', key: 'status', sort: true,
    labelMap: { 'draft': 'Borrador', 'sent': 'Enviada', 'paid': 'Pagada', 'overdue': 'Vencida', 'cancelled': 'Anulada', 'rectified': 'Rectificada' },
    colorMap: { 'draft': 'bg-gray-100 text-gray-800', 'sent': 'bg-blue-100 text-blue-800', 'paid': 'bg-green-100 text-green-800', 'overdue': 'bg-red-100 text-red-800', 'cancelled': 'bg-red-50 text-red-600', 'rectified': 'bg-amber-100 text-amber-800' }
  },
  {
    name: 'Método Pago', active: true, type: 'text', key: 'payment_method_label', sort: false
  },
  {
    name: 'Rectificativa', active: true, type: 'badge', key: 'is_corrective', sort: true,
    labelMap: { 'true': 'Rectificativa', 'false': 'Normal' },
    colorMap: { 'true': 'bg-orange-100 text-orange-800 border border-orange-300', 'false': 'bg-gray-50 text-gray-500' }
  }
];

const formFields: GenericFormField[] = [
  {
    name: 'invoice_type',
    type: 'select',
    label: 'Tipo de Factura',
    validations: 'required',
    optionsList: [
      { id: 'issued', name: 'A emitir' },
      { id: 'received', name: 'A recibir' },
      { id: 'proforma', name: 'Proforma' }
    ],
    singleSelection: true,
    colspan: 2
  },
  { name: 'due_date', type: 'date', label: 'Fecha de Vencimiento', validations: '', colspan: 2 },
  { name: 'user_id', type: 'select', label: 'Alumno', url: '/user/getAll', validations: 'required', singleSelection: true, colspan: 1 },
  { name: 'company_id', type: 'select', label: 'Sociedad', url: '/config/company/listAll', validations: '', singleSelection: true, colspan: 1 },
  { name: 'subtotal', type: 'number', label: 'Base Imponible', validations: 'required|min:0', colspan: 1 },
  { name: 'tax_rate_id', type: 'select', label: 'Tipo de IVA', url: '/tax-rate/all?active=true&type=tax', validations: '', singleSelection: true, colspan: 1 },
  { name: 'retention_rate_id', type: 'select', label: 'Retención', url: '/tax-rate/all?active=true&type=retention', validations: '', singleSelection: true, colspan: 2 },
  {
    name: 'payment_method',
    type: 'select',
    label: 'Método de Pago',
    validations: '',
    optionsList: [
      { id: 'cash', name: 'Efectivo' },
      { id: 'transfer', name: 'Transferencia' },
      { id: 'card', name: 'Tarjeta' },
      { id: 'direct_debit', name: 'Domiciliación' },
      { id: 'split', name: 'Fraccionado' },
      { id: 'wallet', name: 'Monedero digital' }
    ],
    singleSelection: true,
    colspan: 1
  },
  {
    name: 'pos_terminal_id',
    type: 'select',
    label: 'TPV',
    url: '/config/pos-terminal/listAll',
    validations: '',
    singleSelection: true,
    colspan: 1,
    showIf: (item?: any) => {
      const pm = item?.payment_method;
      const method = typeof pm === 'object' ? pm?.id : pm;
      return method === 'card' || method === 'split';
    }
  },
  {
    name: 'cash_amount',
    type: 'number',
    label: 'Importe en Efectivo (€)',
    validations: 'min:0',
    colspan: 1,
    showIf: (item?: any) => {
      const pm = item?.payment_method;
      const method = typeof pm === 'object' ? pm?.id : pm;
      return method === 'split';
    },
  },
  {
    name: 'card_amount',
    type: 'number',
    label: 'Importe en Tarjeta (€)',
    validations: 'min:0',
    colspan: 1,
    showIf: (item?: any) => {
      const pm = item?.payment_method;
      const method = typeof pm === 'object' ? pm?.id : pm;
      return method === 'split';
    },
  },
  {
    name: 'status',
    type: 'select',
    label: 'Estado',
    validations: 'required',
    optionsList: [
      { id: 'draft', name: 'Borrador' },
      { id: 'sent', name: 'Enviada' },
      { id: 'paid', name: 'Pagada' },
      { id: 'overdue', name: 'Vencida' },
      { id: 'cancelled', name: 'Anulada' }
    ],
    singleSelection: true,
    colspan: 1
  },
];

const splitPaymentFields: GenericFormField[] = [
  {
    name: 'pos_terminal_id',
    type: 'select',
    label: 'TPV',
    url: '/config/pos-terminal/listAll',
    validations: '',
    singleSelection: true,
    colspan: 1,
    showIf: (item?: any) => {
      const pm = item?.payment_method;
      const method = typeof pm === 'object' ? pm?.id : pm;
      return method === 'card' || method === 'split';
    }
  },
  {
    name: 'cash_amount',
    type: 'number',
    label: 'Importe en Efectivo (€)',
    validations: 'min:0',
    colspan: 1,
    showIf: (item?: any) => {
      const pm = item?.payment_method;
      const method = typeof pm === 'object' ? pm?.id : pm;
      return method === 'split';
    },
  },
  {
    name: 'card_amount',
    type: 'number',
    label: 'Importe en Tarjeta (€)',
    validations: 'min:0',
    colspan: 1,
    showIf: (item?: any) => {
      const pm = item?.payment_method;
      const method = typeof pm === 'object' ? pm?.id : pm;
      return method === 'split';
    },
  }
];

const formFieldsEdit: GenericFormField[] = [
  { name: 'invoice_date', type: 'date', label: 'Fecha', validations: 'required', colspan: 1 },
  { name: 'due_date', type: 'date', label: 'Fecha de Vencimiento', validations: '', colspan: 1 },
  { name: 'user_id', type: 'select', label: 'Alumno', url: '/user/getAll', validations: 'required', singleSelection: true, colspan: 1 },
  { name: 'company_id', type: 'select', label: 'Sociedad', url: '/config/company/listAll', validations: '', singleSelection: true, colspan: 1 },
  { name: 'subtotal', type: 'number', label: 'Base Imponible', validations: 'required|min:0', colspan: 1 },
  { name: 'tax_rate_id', type: 'select', label: 'Tipo de IVA', url: '/tax-rate/all?active=true&type=tax', validations: '', singleSelection: true, colspan: 1 },
  { name: 'retention_rate_id', type: 'select', label: 'Retención', url: '/tax-rate/all?active=true&type=retention', validations: '', singleSelection: true, colspan: 2 },
  {
    name: 'payment_method',
    type: 'select',
    label: 'Método de Pago',
    validations: '',
    optionsList: [
      { id: 'cash', name: 'Efectivo' },
      { id: 'transfer', name: 'Transferencia' },
      { id: 'card', name: 'Tarjeta' },
      { id: 'direct_debit', name: 'Domiciliación' },
      { id: 'split', name: 'Fraccionado' },
      { id: 'wallet', name: 'Monedero digital' }
    ],
    singleSelection: true,
    colspan: 1
  },
  ...splitPaymentFields,
  {
    name: 'status',
    type: 'select',
    label: 'Estado',
    validations: 'required',
    optionsList: [
      { id: 'draft', name: 'Borrador' },
      { id: 'sent', name: 'Enviada' },
      { id: 'paid', name: 'Pagada' },
      { id: 'overdue', name: 'Vencida' },
      { id: 'cancelled', name: 'Anulada' }
    ],
    singleSelection: true,
    colspan: 1
  },
];

const tableActions: GenericTableAction[] = [
  {
    name: 'Eliminar',
    key: 'delete',
    method: 'DELETE',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>',
    confirmation: true,
    questionText: '¿Desea eliminar esta factura?',
    deleteRowOnResponse: true,
    actionSlug: '/invoice/delete/{{id}}'
  }
];

const filters: Filter[] = [
  { type: 'search', key: 'invoice_number|client.name', operator: 'like', placeholder: 'Número de factura o cliente' },
  {
    type: 'date-between',
    key: 'invoice_date',
    operator: 'between',
    placeholder: 'Fecha de emisión'
  },
  {
    type: 'date-between',
    key: 'due_date',
    operator: 'between',
    placeholder: 'Fecha de vencimiento'
  },
  {
    type: 'select',
    key: 'invoice_type',
    operator: 'in',
    placeholder: 'Tipo de Factura',
    optionsList: [
      { id: 'issued', name: 'A emitir' },
      { id: 'received', name: 'A recibir' },
      { id: 'proforma', name: 'Proforma' }
    ]
  },
  {
    type: 'select',
    key: 'status',
    operator: 'in',
    placeholder: 'Estado',
    optionsList: [
      { id: 'draft', name: 'Borrador' },
      { id: 'sent', name: 'Enviada' },
      { id: 'paid', name: 'Pagada' },
      { id: 'overdue', name: 'Vencida' },
      { id: 'cancelled', name: 'Anulada' },
      { id: 'rectified', name: 'Rectificada' }
    ]
  },
  {
    type: 'select',
    key: 'paid',
    operator: 'in',
    placeholder: 'Estado de Pago',
    optionsList: [
      { id: '1', name: 'Pagada' },
      { id: '0', name: 'Pendiente' }
    ]
  },
  {
    type: 'select',
    key: 'payment_method',
    operator: 'in',
    placeholder: 'Método de Pago',
    optionsList: [
      { id: 'cash', name: 'Efectivo' },
      { id: 'transfer', name: 'Transferencia' },
      { id: 'card', name: 'Tarjeta' },
      { id: 'direct_debit', name: 'Domiciliación' },
      { id: 'split', name: 'Fraccionado' },
      { id: 'wallet', name: 'Monedero digital' }
    ]
  },
  {
    type: 'select',
    key: 'user_id',
    operator: 'in',
    placeholder: 'Alumno',
    url: '/user/getAll'
  },
  {
    type: 'select',
    key: 'is_corrective',
    operator: 'in',
    placeholder: 'Categoría',
    optionsList: [
      { id: '0', name: 'Normales' },
      { id: '1', name: 'Rectificativas' }
    ]
  }
];

/**
 * Configuración completa de la entidad Facturas
 * Incluye facturas emitidas, recibidas y proformas
 */
export const invoicesConfig: EntityConfig = {
  id: 'invoices',
  name: 'Facturas',
  icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',

  tableColumns,
  formFields,
  formFieldsEdit,
  tableActions,
  filters,

  form: {
    successTitle: 'Factura creada correctamente',
    errorTitle: 'Error al crear factura',
    method: 'POST',
    fields: formFields,
    name: 'factura',
    createButtonTitle: 'Nueva Factura',
    cols: 2,
    createApiSlug: '/invoice/store',
    formTitle: 'Crear Factura',
    formButtonTitle: 'Crear Factura',
    finishLabel: 'Crear Factura',
    width: '4xl',
    steps: [
      { label: 'Datos generales', fieldNames: ['invoice_type', 'user_id', 'company_id', 'due_date', 'status'] },
      { label: 'Importes', fieldNames: ['subtotal', 'tax_rate_id', 'retention_rate_id'] },
      { label: 'Metodo de pago', fieldNames: ['payment_method', 'pos_terminal_id', 'cash_amount', 'card_amount'] },
    ]
  },

  table: {
    columns: tableColumns,
    items: [],
    type: 'generic',
    loadItemsSlug: '/invoice/index',
    paginate: true,
    name: 'facturas',
    actions: tableActions,
    details: true,
    detailsSlug: '/dashboard/administracion/invoices/{{id}}',
    defaultSort: { column: 'created_at', order: 'desc' }
  },

  useModalForm: false
};
